import {
  Box,
  InputAdornment,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useCallback, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import { useStudio, type Direction, type StudioItem } from '../../context';
import {
  LAYERS,
  StudioSideBySide,
  StudioSideBySideContainer,
  StudioSidebarInterior,
  StudioSidebarTitle,
  rotateAroundCenter,
} from '../lib';

export interface StudioSettingsFormatTabPositionProps {
  selectedItem: StudioItem;
}

export const StudioSettingsFormatTabPosition = ({
  selectedItem,
}: StudioSettingsFormatTabPositionProps) => {
  const disabled = selectedItem.locked;
  const { t } = useTranslation();
  const { moveItem, updateItems } = useStudio();

  const handleMove = useCallback(
    (_: MouseEvent, direction: Direction) => moveItem(selectedItem.id, direction),
    [moveItem, selectedItem.id],
  );

  // A RegularPolygon has a single size, so width, height and radius move together.
  const handleResize = useCallback(
    (dimension: 'height' | 'width', value: number) => {
      const size = Math.max(1, value);

      updateItems([
        selectedItem.type === 'triangle'
          ? { height: size, id: selectedItem.id, radius: size / 2, width: size }
          : { [dimension]: size, id: selectedItem.id },
      ]);
    },
    [selectedItem.id, selectedItem.type, updateItems],
  );

  return (
    <StudioSidebarInterior>
      <StudioSideBySideContainer>
        <StudioSideBySide>
          <StudioSidebarTitle>
            {t('pages.studio.edit.settings.position.transparency')}
          </StudioSidebarTitle>
        </StudioSideBySide>

        <StudioSideBySide>
          <Slider
            disabled={disabled}
            min={0}
            max={1}
            onChange={(_, opacity) => updateItems([{ id: selectedItem.id, opacity }])}
            step={0.05}
            value={selectedItem.opacity ?? 1}
            valueLabelDisplay="auto"
          />
        </StudioSideBySide>
      </StudioSideBySideContainer>

      <StudioSidebarTitle>
        {t('pages.studio.edit.settings.position.position_size')}
      </StudioSidebarTitle>
      <Box>
        <StudioSideBySideContainer sx={{ paddingBottom: 2 }}>
          <StudioSideBySide>
            <TextField
              disabled={disabled}
              InputProps={{
                startAdornment: <InputAdornment position="start">W</InputAdornment>,
                endAdornment: <InputAdornment position="end">px</InputAdornment>,
                inputProps: { min: 1 },
              }}
              onChange={(event) => handleResize('width', Number(event.target.value))}
              type="number"
              value={selectedItem.width}
            />
          </StudioSideBySide>
          <StudioSideBySide>
            <TextField
              disabled={disabled || selectedItem.type === 'text'}
              InputProps={{
                startAdornment: <InputAdornment position="start">H</InputAdornment>,
                endAdornment: <InputAdornment position="end">px</InputAdornment>,
                inputProps: { min: 1 },
              }}
              onChange={(event) => handleResize('height', Number(event.target.value))}
              type={selectedItem.type === 'text' ? 'text' : 'number'}
              value={selectedItem.type === 'text' ? 'auto' : selectedItem.height}
            />
          </StudioSideBySide>
        </StudioSideBySideContainer>
        <StudioSideBySideContainer sx={{ paddingBottom: 2 }}>
          <StudioSideBySide>
            <TextField
              disabled={disabled}
              InputProps={{
                startAdornment: <InputAdornment position="start">X</InputAdornment>,
                endAdornment: <InputAdornment position="end">px</InputAdornment>,
              }}
              onChange={(event) =>
                updateItems([{ id: selectedItem.id, x: Number(event.target.value) }])
              }
              type="number"
              value={selectedItem.x}
            />
          </StudioSideBySide>
          <StudioSideBySide>
            <TextField
              disabled={disabled}
              InputProps={{
                startAdornment: <InputAdornment position="start">Y</InputAdornment>,
                endAdornment: <InputAdornment position="end">px</InputAdornment>,
              }}
              onChange={(event) =>
                updateItems([{ id: selectedItem.id, y: Number(event.target.value) }])
              }
              type="number"
              value={selectedItem.y}
            />
          </StudioSideBySide>
        </StudioSideBySideContainer>

        <TextField
          disabled={disabled}
          InputProps={{
            startAdornment: <InputAdornment position="start">R</InputAdornment>,
            endAdornment: <InputAdornment position="end">deg</InputAdornment>,
            inputProps: { min: -180, max: 180 },
          }}
          onChange={(event) =>
            updateItems([
              {
                id: selectedItem.id,
                ...rotateAroundCenter(selectedItem, Number(event.target.value)),
              },
            ])
          }
          sx={{ width: '150px' }}
          type="number"
          value={selectedItem.rotation ?? 0}
        />
      </Box>

      <StudioSidebarTitle>{t('pages.studio.edit.settings.position.layer')}</StudioSidebarTitle>
      <Box>
        <ToggleButtonGroup
          aria-label={t('pages.studio.edit.settings.position.layer')}
          disabled={disabled}
          exclusive
          fullWidth
          onChange={handleMove}
        >
          {LAYERS.map(({ Icon, tooltip, value }) => (
            <Tooltip key={value} title={tooltip}>
              <span>
                <ToggleButton
                  aria-label={tooltip}
                  sx={{ fontWeight: 'bold', textTransform: 'none' }}
                  value={value}
                >
                  <Icon />
                </ToggleButton>
              </span>
            </Tooltip>
          ))}
        </ToggleButtonGroup>
      </Box>
    </StudioSidebarInterior>
  );
};
